import type { QueueSummary, QueueTreeNode } from "../../../experience-shell/queues/types";

// Category queues have no members and cannot receive a question. Personal
// queues are the primary target of the Ask flow and stay in.
export function flattenWorkable(roots: readonly QueueTreeNode[]): QueueSummary[] {
    const out: QueueSummary[] = [];
    const visit = (node: QueueTreeNode): void => {
        if (node.queue.isWorkable && node.queue.status === "active") {
            out.push(node.queue);
        }
        for (const child of node.children) {
            visit(child);
        }
    };
    for (const root of roots) {
        visit(root);
    }
    return out;
}

// Personal queues are named "{displayName}'s Queue". The UI shows the
// person, so the suffix is removed for display only.
const PERSONAL_QUEUE_SUFFIX = "'s Queue";
export function displayNameFor(queue: QueueSummary): string {
    if (queue.isPersonal && queue.name.endsWith(PERSONAL_QUEUE_SUFFIX)) {
        return queue.name.slice(0, -PERSONAL_QUEUE_SUFFIX.length);
    }
    return queue.name;
}

// Per-group cap. The list stays within six rows per group so it reads as
// a suggestion strip, not a directory.
export const MAX_PER_GROUP = 6;

export interface RecipientCandidates {
    readonly people: readonly QueueSummary[];
    readonly queues: readonly QueueSummary[];
}

// People first: person-to-person questions are the primary intent.
export function splitCandidates(
    allWorkable: readonly QueueSummary[],
    selectedIds: readonly string[],
    excludedIds: readonly string[] | undefined,
    filter: string,
): RecipientCandidates {
    const selected = new Set(selectedIds);
    const excluded = new Set(excludedIds ?? []);
    const needle = filter.trim().toLowerCase();
    const peopleAcc: QueueSummary[] = [];
    const queuesAcc: QueueSummary[] = [];
    for (const q of allWorkable) {
        if (selected.has(q.id) || excluded.has(q.id)) continue;
        if (needle.length > 0) {
            const hay = [displayNameFor(q), q.name, ...q.ancestorPath].join(" / ").toLowerCase();
            if (!hay.includes(needle)) continue;
        }
        (q.isPersonal ? peopleAcc : queuesAcc).push(q);
    }
    return {
        people: peopleAcc.slice(0, MAX_PER_GROUP),
        queues: queuesAcc.slice(0, MAX_PER_GROUP),
    };
}

function plural(count: number, one: string, many: string): string {
    return `${String(count)} ${count === 1 ? one : many}`;
}

/** "To 2 people · 1 queue". Empty string when nothing is selected. */
export function summarizeRecipientCounts(
    recipientIds: readonly string[],
    personalQueueIds: ReadonlySet<string>,
): string {
    let people = 0;
    let queues = 0;
    for (const id of recipientIds) {
        if (personalQueueIds.has(id)) people += 1;
        else queues += 1;
    }
    const parts: string[] = [];
    if (people > 0) parts.push(plural(people, "person", "people"));
    if (queues > 0) parts.push(plural(queues, "queue", "queues"));
    return parts.length === 0 ? "" : `To ${parts.join(" \u00B7 ")}`;
}

// ---------------------------------------------------------------------------
// Sender ("From")
// ---------------------------------------------------------------------------

/** Sendable queues, the asker's own queue first, then shared queues by name. */
export function sendAsOptions(queues: readonly QueueSummary[] | undefined): QueueSummary[] {
    if (queues === undefined) return [];
    const active = queues.filter((q) => q.isWorkable && q.status === "active");
    const personal = active.filter((q) => q.isPersonal);
    const shared = active.filter((q) => !q.isPersonal).sort((a, b) => a.name.localeCompare(b.name));
    return [...personal.slice(0, 1), ...shared];
}

/** The asker's own queue reads as "You"; a shared queue reads by name. */
export function senderLabel(queue: QueueSummary): string {
    return queue.isPersonal ? "You" : queue.name;
}

/**
 * Initial sender: `preferredId` when it is sendable, else the asker's own
 * queue, else the first option. Undefined when there is nothing to send from.
 */
export function resolveDefaultSenderId(
    options: readonly QueueSummary[],
    preferredId: string | undefined,
): string | undefined {
    if (preferredId !== undefined && options.some((q) => q.id === preferredId)) {
        return preferredId;
    }
    return (options.find((q) => q.isPersonal) ?? options[0])?.id;
}
